import {Calculation, Numeric, Language, Translations, EnumerationsLogic, UserDefinedRow, CalculationRowType, IncomeTypesLogic} from "@salaxy/core";
/**
 * Helper class used to create RowsReport config for sxy-table and create tableData for ExcelHelpers(ng1)
 */
export class RowsReportConfigGenerator{
  /**
   * Generates config for SxyTable component
   * @param calcs Calculations for report
   * @param lang Language for report
   * @returns Configuration for SxyTable component
   */
  public static getConfig(calcs: Calculation[], lang: Language):{ options: any, include: any[], data: any[]} {
    const getData = (): any[] => {
      const result: any[] = [];
      calcs.forEach( calc => {
        calc.rows?.forEach(row => {
          const resultRow: any = {};
          resultRow.worker = calc.worker?.avatar?.displayName;
          resultRow.socSec = calc.worker?.paymentData?.socialSecurityNumber;
          resultRow.message = row.message;
          resultRow.rowType = row.rowType;
          resultRow.rowTypeKind = this.getKind(row, lang);
          resultRow.type = row.type;
          resultRow.sourceId = row.sourceId;
          resultRow.count = row.count;
          resultRow.price = row.price;
          resultRow.total = (row.count ?? 1) * (row.price ?? 0);
          getDimensionsKeys().forEach(key => {
            resultRow[key] = row.accounting?.dimensions?.filter(x => x.id === key).map(dimension => dimension.value).join(", ");
          });

          result.push(resultRow);
        });
      });
      return result;
    };
    const getDimensionsKeys = (): string[] => {
      const result: any[] = [];
      const flattenedDimensions = calcs.map(calc => calc.rows && calc.rows.map(row => row.accounting && row.accounting.dimensions).flat()).flat();
      flattenedDimensions.forEach(dimension => {
        if(dimension && !result.includes(dimension.id)) {
          result.push(dimension.id);
        }
      });
      return result;
    };
    const getDimensionColumDefitions = (dimensions: string[]): any[] => {
      return dimensions.map(dimension => {
        return {title: dimension, field: dimension};
      });
    };
    const getConfig =  ():{ options: any, include: any[], data: any[]}  => {
      return {
        include: [
          {title: Translations.get("SALAXY.REPORTS.rowsReport.worker", null, lang), field: "worker", frozen: true, minWidth: 100},
          {title: Translations.get("SALAXY.REPORTS.rowsReport.socsec", null, lang), field: "socSec"},
          {title: Translations.get("SALAXY.REPORTS.rowsReport.message", null, lang), field: "message"},
          {title: Translations.get("SALAXY.REPORTS.rowsReport.rowType", null, lang), field: "rowType", formatter: (cell: any) =>{
            return EnumerationsLogic.getEnumLabel("CalculationRowType", cell.getValue(), undefined, lang) ?? "";
          }},
          {title: Translations.get("SALAXY.REPORTS.rowsReport.rowTypeKind", null, lang), field: "rowTypeKind"},
          {title: Translations.get("SALAXY.REPORTS.rowsReport.type", null, lang), field: "type"},
          {title: Translations.get("SALAXY.REPORTS.rowsReport.sourceId", null, lang), field: "sourceId"},
          {title: Translations.get("SALAXY.REPORTS.rowsReport.count", null, lang), field: "count"},
          {title: Translations.get("SALAXY.REPORTS.rowsReport.price", null, lang), field: "price", hozAlign: "right", formatter: "money", formatterParams: { symbol: "€", symbolAfter: true, decimal: ",", thousand: " ", }},
          {title: Translations.get("SALAXY.REPORTS.rowsReport.total", null, lang), field: "total", hozAlign: "right", formatter: "money", formatterParams: { symbol: "€", symbolAfter: true, decimal: ",", thousand: " ", }},
          ...getDimensionColumDefitions(getDimensionsKeys())
        ],
        options: {
          selectableRows: false,
          layout: "fitDataStretch"
        },
        data: getData()
      };
    };
    return getConfig();
  }

  private static getKind(row: UserDefinedRow, lang: Language): string {
    if(!row.data?.kind) {
      return "";
    }
    const rowType = row.rowType;
    if(rowType === CalculationRowType.IrIncomeType) {
      return IncomeTypesLogic.getAll(lang).find( (x) => (row.data?.kind && x.transactionCode === row.data?.kind) || (row?.data?.code && x.code === row?.data?.code))?.label ?? "";
    } else {
      const kindType = (row.rowType as string).charAt(0).toUpperCase() + row.rowType?.slice(1)  + "Kind";
      return EnumerationsLogic.getEnumLabel(kindType, row.data?.kind) ?? "";
    }
  }

  /** Generates tableData for ExcelHelpers.export (ng1) */
  public static getTableData(calcs: Calculation[], lang: Language): any[] {
    const result: any[] = [];
    const getDimensionsKeys = (): string[] => {
      const result: any[] = [];
      const flattenedDimensions = calcs.map(calc => calc.rows && calc.rows.map(row => row.accounting && row.accounting.dimensions).flat()).flat();
      flattenedDimensions.forEach(dimension => {

        if(dimension && !result.includes(dimension.id)) {
          result.push(dimension.id);
        }
      });

      return result;
    };
    const dimensionKeys = getDimensionsKeys();
    const headers: any[] = [
      Translations.get("SALAXY.REPORTS.rowsReport.worker"),
      Translations.get("SALAXY.REPORTS.rowsReport.socsec", null, lang),
      Translations.get("SALAXY.REPORTS.rowsReport.message", null, lang),
      Translations.get("SALAXY.REPORTS.rowsReport.rowType", null, lang),
      Translations.get("SALAXY.REPORTS.rowsReport.rowTypeKind", null, lang),
      Translations.get("SALAXY.REPORTS.rowsReport.type", null, lang),
      Translations.get("SALAXY.REPORTS.rowsReport.sourceId", null, lang),
      Translations.get("SALAXY.REPORTS.rowsReport.count", null, lang),
      Translations.get("SALAXY.REPORTS.rowsReport.price", null, lang),
      Translations.get("SALAXY.REPORTS.rowsReport.total", null, lang)
    ];
    dimensionKeys.forEach(key => {
      headers.push(key);
    });
    result.push(headers);
    calcs.forEach(calc => {
      calc.rows?.forEach(userDefinedRow => {
        const row: any[] = [];
        row.push(calc.worker?.avatar?.displayName);
        row.push(calc.worker?.paymentData?.socialSecurityNumber);
        row.push(userDefinedRow.message);
        row.push(EnumerationsLogic.getEnumLabel("CalculationRowType", userDefinedRow.rowType ?? "", undefined, lang));
        row.push(this.getKind(userDefinedRow,lang));
        row.push(userDefinedRow.type);
        row.push(userDefinedRow.sourceId);
        row.push(userDefinedRow.count);
        row.push(userDefinedRow.price);
        row.push(Numeric.formatPrice((userDefinedRow.count ?? 1) * (userDefinedRow.price ?? 0)));
        dimensionKeys.forEach(key => {
          const result = userDefinedRow.accounting?.dimensions?.filter(x => x.id === key).map(dimension => dimension.value).join(", ");
          row.push(result);
        });

        result.push(row);
      });
    });
    return result;
  }
}